import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import type { U2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { U3125InvoiceFormRow } from "../../../../U3125Service";
import {
  u3125ApplyWipSelectionToFormRow,
  u3125FilterWipRowsByKind,
  type U3125TaxRateInitItem,
  type U3125WipTransactionKindFilter,
} from "../../../../U3125Service";
import { useU3125GetWipTransactionsHandlers } from "../../../../U3125UseApiHandlers";
import { NS } from "../../../../U3125";
import { U3134_TRANSACTION_KIND } from "../../../../../u3134WorkInProgress/U3134Service";
import type { U3134WorkInProgressSearchRow } from "../../../../../u3134WorkInProgress/U3134Service";
import U3125WipTransactionTable from "../U3125WipTransactionTable";

export interface U3125WipAssociateDlgProps {
  isOpen: boolean;
  onClose: () => void;
  formStateHandlers: U2000FormStateHandlers<U3125InvoiceFormRow>;
  projectId: string;
  taxRates: U3125TaxRateInitItem[];
  onApplied: (rows: U3134WorkInProgressSearchRow[]) => void;
}

const WIP_KIND_FILTERS: { key: U3125WipTransactionKindFilter; labelKey: string; kind: U3125WipTransactionKindFilter }[] = [
  { key: U3134_TRANSACTION_KIND.Honorarium, labelKey: "btnWipFilterEmployee", kind: U3134_TRANSACTION_KIND.Honorarium },
  { key: U3134_TRANSACTION_KIND.Expense, labelKey: "btnWipFilterExpense", kind: U3134_TRANSACTION_KIND.Expense },
  { key: U3134_TRANSACTION_KIND.Equipment, labelKey: "btnWipFilterEquipment", kind: U3134_TRANSACTION_KIND.Equipment },
];

const DEFAULT_WIP_KIND_FILTERS = new Set<U3125WipTransactionKindFilter>(
  WIP_KIND_FILTERS.map(({ kind }) => kind),
);

const U3125WipAssociateDlg: React.FC<U3125WipAssociateDlgProps> = ({
  isOpen,
  onClose,
  formStateHandlers,
  projectId,
  taxRates,
  onApplied,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<U3134WorkInProgressSearchRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeKindFilters, setActiveKindFilters] = useState<Set<U3125WipTransactionKindFilter>>(() => new Set(DEFAULT_WIP_KIND_FILTERS));
  const getWipTransactionsHandlers = useU3125GetWipTransactionsHandlers();

  useEffect(() => {
    if (!isOpen || !projectId.trim()) return;
    let cancelled = false;
    setLoading(true);
    setActiveKindFilters(new Set(DEFAULT_WIP_KIND_FILTERS));
    const invoiceId = formStateHandlers.getData()?.id?.trim() || undefined;
    void (async () => {
      const list = await getWipTransactionsHandlers.fetch(projectId, invoiceId);
      if (cancelled) return;
      setRows(list);
      const currentIds = formStateHandlers.getData()?.workInProgressLineIds ?? [];
      const pre = new Set(currentIds.filter((id) => list.some((r) => r.id === id)));
      setSelectedIds(pre);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formStateHandlers.getData() à l’ouverture uniquement
  }, [isOpen, projectId, getWipTransactionsHandlers]);

  const filteredRows = useMemo(() => u3125FilterWipRowsByKind(rows, activeKindFilters), [rows, activeKindFilters]);

  const toggleKindFilter = useCallback(
    (kind: U3125WipTransactionKindFilter) => {
      const wasActive = activeKindFilters.has(kind);
      setActiveKindFilters((prev) => {
        const next = new Set(prev);
        if (wasActive) next.delete(kind);
        else next.add(kind);
        return next;
      });
      if (wasActive) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const row of rows) {
            if (row.transactionKind === kind) next.delete(row.id);
          }
          return next;
        });
      } else {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const row of rows) {
            if (row.transactionKind === kind) next.add(row.id);
          }
          return next;
        });
      }
    },
    [rows, activeKindFilters],
  );

  const toggleOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleHeader = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const row of filteredRows) {
          if (checked) next.add(row.id);
          else next.delete(row.id);
        }
        return next;
      });
    },
    [filteredRows],
  );

  const headerChecked = filteredRows.length > 0 && filteredRows.every((r) => selectedIds.has(r.id));
  const headerIndeterminate = !headerChecked && filteredRows.some((r) => selectedIds.has(r.id));

  const handleDialogClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      onClose();
      return;
    }
    const d = formStateHandlers.getData();
    if (!d) {
      onClose();
      return;
    }
    const next = Array.from(selectedIds);
    formStateHandlers.setData(u3125ApplyWipSelectionToFormRow(d, rows, next, taxRates));
    onApplied(rows.filter((r) => selectedIds.has(r.id)));
    onClose();
  };

  const filterActions = useMemo(
    () =>
      WIP_KIND_FILTERS.map(({ key, labelKey, kind }) => ({
        key,
        label: t(labelKey, { ns: NS }),
        handleClick: () => toggleKindFilter(kind),
        hasRole: true,
        disabled: loading || rows.length === 0,
        variant: activeKindFilters.has(kind) ? "primary" : "secondary",
        Component: U2000ActionBtn,
      })),
    [t, toggleKindFilter, loading, rows.length, activeKindFilters],
  );

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleDialogClose}
      title={t("dlgWipAssociateTitle", { ns: NS })}
      width="w-[95vw] max-w-[1600px]"
      height="max-h-[90vh]"
      isValid={!loading}
    >
      <div className="flex min-h-0 flex-col gap-3">
        <U2000ResponsiveToolbar actions={filterActions} />
        {loading ? (
          <p className="text-sm text-gray-600">{t("lblLoading", { ns: NS })}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-600">{t("msgNoWipLines", { ns: NS })}</p>
        ) : activeKindFilters.size === 0 ? (
          <p className="text-sm text-gray-600">{t("msgWipSelectFilter", { ns: NS })}</p>
        ) : filteredRows.length === 0 ? (
          <p className="text-sm text-gray-600">{t("msgNoWipLinesForFilter", { ns: NS })}</p>
        ) : (
          <U3125WipTransactionTable
            rows={filteredRows}
            variant="full"
            selectedIds={selectedIds}
            onToggleOne={toggleOne}
            headerChecked={headerChecked}
            headerIndeterminate={headerIndeterminate}
            onToggleHeader={toggleHeader}
          />
        )}
        <p className="text-xs text-gray-500">{t("dlgWipAssociateHint", { ns: NS })}</p>
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3125WipAssociateDlg;
